import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Calendar, Clock, BookOpen, Filter, Users, FileText } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов генератора расписания
import GeneratorForm from './ScheduleGeneratorPage/components/GeneratorForm';
import ScheduleDisplay from './ScheduleGeneratorPage/components/ScheduleDisplay';
import ScheduleActions from './ScheduleGeneratorPage/components/ScheduleActions';
import TemplatesSidebar from './ScheduleGeneratorPage/components/TemplatesSidebar';
import SettingsSidebar from './ScheduleGeneratorPage/components/SettingsSidebar';
import HelpInfoSidebar from './ScheduleGeneratorPage/components/HelpInfoSidebar';
import InfoContent from './ScheduleGeneratorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  scheduleType: 'weekly', // 'weekly', 'monthly', 'semester', 'custom'
  startDate: new Date().toISOString().split('T')[0],
  endDate: new Date(new Date().setMonth(new Date().getMonth() + 3)).toISOString().split('T')[0],
  workDays: ['1', '2', '3', '4', '5'], // 0 - воскресенье, 1 - понедельник, и т.д.
  dayStartTime: '09:00',
  dayEndTime: '18:00',
  lunchBreak: true,
  lunchStartTime: '13:00',
  lunchDuration: 60, // минуты
  holidaysInclude: true,
  customHolidays: [],
  eventDuration: 45, // минуты
  breakDuration: 10, // минуты
  subjects: [
    { id: '1', name: 'Математика', color: '#4F46E5', hoursPerWeek: 4, teacher: 'Иванов И.И.' },
    { id: '2', name: 'Русский язык', color: '#16A34A', hoursPerWeek: 3, teacher: 'Петрова П.П.' },
    { id: '3', name: 'Информатика', color: '#CA8A04', hoursPerWeek: 2, teacher: 'Сидоров С.С.' }
  ],
  rooms: [
    { id: '1', name: '101', capacity: 30, equipment: ['проектор', 'компьютеры'] },
    { id: '2', name: '102', capacity: 25, equipment: ['проектор'] },
    { id: '3', name: '103', capacity: 20, equipment: ['лабораторное оборудование'] }
  ],
  groups: [
    { id: '1', name: '11А', size: 25 },
    { id: '2', name: '11Б', size: 23 }
  ],
  teachers: [
    { id: '1', name: 'Иванов И.И.', subjects: ['1'], availability: [] },
    { id: '2', name: 'Петрова П.П.', subjects: ['2'], availability: [] },
    { id: '3', name: 'Сидоров С.С.', subjects: ['3'], availability: [] }
  ],
  optimizationRules: {
    distributeEvenly: true, // Равномерное распределение предметов
    avoidLateStart: true, // Избегать позднего начала занятий
    prioritizeRoomPreferences: true, // Учитывать предпочтения по аудиториям
    balanceLoad: true // Сбалансировать нагрузку
  }
};

// Определение типов расписаний
export const scheduleTypes = [
  { id: 'weekly', name: 'Недельное расписание', description: 'Стандартное недельное расписание занятий' },
  { id: 'monthly', name: 'Месячное расписание', description: 'Расписание на месяц вперед' },
  { id: 'semester', name: 'Семестровое расписание', description: 'Полное расписание на учебный семестр' },
  { id: 'custom', name: 'Произвольный период', description: 'Настраиваемый период расписания' }
];

/**
 * ScheduleGeneratorPage Component - Генератор расписаний для учебных заведений и организаций.
 */
const ScheduleGeneratorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('schedule-generator'), []);
  const pageTitle = tool?.metaTitle || 'Генератор расписаний | Gensio';
  const metaDescription = tool?.metaDescription || 'Удобный генератор расписаний для школ, колледжей, вузов и организаций. Создавайте оптимальные учебные расписания с учетом доступности кабинетов, нагрузки преподавателей и образовательных стандартов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [generatedSchedule, setGeneratedSchedule] = useState(null);
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedTemplates, setSavedTemplates] = useState([]);
  const [activeView, setActiveView] = useState('week'); // 'week', 'day', 'month', 'list'
  const [notifications, setNotifications] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных шаблонов из localStorage ---
  useEffect(() => {
    const savedTemplatesData = localStorage.getItem('savedScheduleTemplates');
    if (savedTemplatesData) {
      try {
        setSavedTemplates(JSON.parse(savedTemplatesData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных шаблонов:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'school':
          newState.scheduleType = 'weekly';
          // Предустановки для школы
          break;
        case 'university':
          newState.scheduleType = 'semester';
          newState.eventDuration = 90; // Длина пары в университете
          // Предустановки для университета
          break;
        case 'courses':
          newState.scheduleType = 'monthly';
          // Предустановки для курсов
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setGeneratedSchedule(null);
    setErrors({});

  }, [subToolId]);

  // Сохранение шаблона расписания в localStorage
  const saveTemplate = (name) => {
    if (!formData) return;

    const newTemplate = {
      id: Date.now().toString(),
      name: name || `Расписание от ${new Date().toLocaleDateString()}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      scheduleType: formData.scheduleType
    };

    const updatedTemplates = [...savedTemplates, newTemplate];
    setSavedTemplates(updatedTemplates);
    localStorage.setItem('savedScheduleTemplates', JSON.stringify(updatedTemplates));

    // Добавляем уведомление
    setNotifications([
      ...notifications,
      {
        id: Date.now(),
        type: 'success',
        message: `Шаблон "${newTemplate.name}" успешно сохранен`
      }
    ]);

    return newTemplate;
  };

  // Удаление сохраненного шаблона
  const deleteTemplate = (id) => {
    const templateToDelete = savedTemplates.find(template => template.id === id);
    const updatedTemplates = savedTemplates.filter(template => template.id !== id);
    setSavedTemplates(updatedTemplates);
    localStorage.setItem('savedScheduleTemplates', JSON.stringify(updatedTemplates));

    // Добавляем уведомление
    setNotifications([
      ...notifications,
      {
        id: Date.now(),
        type: 'info',
        message: `Шаблон "${templateToDelete?.name || 'Unnamed'}" удален`
      }
    ]);
  };

  // Загрузка сохраненного шаблона
  const loadTemplate = (id) => {
    const templateToLoad = savedTemplates.find(template => template.id === id);
    if (templateToLoad) {
      setFormData(templateToLoad.formData);
      setGeneratedSchedule(null);

      // Добавляем уведомление
      setNotifications([
        ...notifications,
        {
          id: Date.now(),
          type: 'info',
          message: `Шаблон "${templateToLoad.name}" загружен`
        }
      ]);
    }
  };

  // Удаление уведомления
  const removeNotification = (id) => {
    setNotifications(notifications.filter(notification => notification.id !== id));
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        {/* Уведомления */}
        <div className="fixed top-20 right-4 w-72 z-50 space-y-2">
          {notifications.map(notification => (
            <div 
              key={notification.id} 
              className={`p-3 rounded-lg shadow-lg flex justify-between items-start ${
                notification.type === 'success' 
                  ? 'bg-green-100 dark:bg-green-900/60 text-green-800 dark:text-green-200' 
                  : notification.type === 'error'
                  ? 'bg-red-100 dark:bg-red-900/60 text-red-800 dark:text-red-200'
                  : 'bg-blue-100 dark:bg-blue-900/60 text-blue-800 dark:text-blue-200'
              }`}
            >
              <p className="text-sm">{notification.message}</p>
              <button 
                onClick={() => removeNotification(notification.id)}
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                ×
              </button>
            </div>
          ))}
        </div>

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Calendar className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Генератор расписаний'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма генератора --- */}
            <GeneratorForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isGenerating={isGenerating}
              setIsGenerating={setIsGenerating}
              generatedSchedule={generatedSchedule}
              setGeneratedSchedule={setGeneratedSchedule}
              scheduleTypes={scheduleTypes}
              saveTemplate={saveTemplate}
            />

            {/* --- Отображение сгенерированного расписания --- */}
            {generatedSchedule && !isGenerating && (
              <>
                <ScheduleActions 
                  activeView={activeView}
                  setActiveView={setActiveView}
                  generatedSchedule={generatedSchedule}
                  formData={formData}
                />
                <ScheduleDisplay 
                  schedule={generatedSchedule} 
                  formData={formData}
                  activeView={activeView}
                />
              </>
            )}

            {/* --- Информационный контент о расписаниях --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель с шаблонами --- */}
            <TemplatesSidebar 
              savedTemplates={savedTemplates}
              deleteTemplate={deleteTemplate}
              loadTemplate={loadTemplate}
            />

            {/* --- Панель настроек --- */}
            <SettingsSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isGenerating={isGenerating}
            />

            {/* --- Полезная информация --- */}
            <HelpInfoSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Schedule Display */
        .schedule-display {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .schedule-display {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .schedule-display::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .schedule-display::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .schedule-display::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .schedule-display::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .schedule-display::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .schedule-display::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .schedule-display::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }

        /* Event Styling */
        .schedule-event {
          @apply rounded-md px-2 py-1 text-xs font-medium shadow-sm transition-all duration-150 cursor-pointer;
        }
        .schedule-event:hover {
          @apply shadow-md transform -translate-y-0.5;
        }
      `}</style>
    </div>
  );
};

export default ScheduleGeneratorPage;